import { FileWriter } from '@/platform'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import BitmapFontData, {
  BitmapFontFile,
  BitmapFontGlyph,
  BitmapFontPackage,
} from '@/model/BitmapFont/BitmapFontData'
import type MainProject from '@/model/MainProject'
import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import EventText from '@/model/EventClass/EventText'
import EventSendAirBubble from '@/model/EventClass/EventSendAirBubble'
import EventTextDif from '@/model/EventClass/EventTextDif'
import EventTextDifEx from '@/model/EventClass/EventTextDifEx'
import EventTextDifEx2 from '@/model/EventClass/EventTextDifEx2'
import EventTextChoice from '@/model/EventClass/EventTextChoice'
import EventString from '@/model/EventClass/EventString'
import EventCallMenu from '@/model/EventClass/EventCallMenu'
import EventCallScene from '@/model/EventClass/EventCallScene'
import type MapInfo from '@/model/MapInfo'
import type DataFileMap from '@/model/Resource/DataFileMap'
import {
  ensureFontLoadedFromVirtualResource,
} from '@/features/project-font/runtime'
import {
  formatCanvasFontFamily,
  type BitmapFontTextMetrics,
  measureBitmapFontTextWithContext,
} from '@/features/bitmap-font/metrics'
import {
  buildFontListBytes,
  buildSaveLoadListBytes,
  collectBitmapFontTargets,
  resolveMainFontSpec,
  toBitmapFontPackageKey,
} from '@/features/bitmap-font/fontSpec'
import { clampBitmapFontSize } from '@/features/bitmap-font/fontSize'
import { stripEmoji } from '@/utils/plainTextInput'

export { clampBitmapFontSize } from '@/features/bitmap-font/fontSize'
export { toBitmapFontPackageKey } from '@/features/bitmap-font/fontSpec'

const ATLAS_MAX_WIDTH = 1024
const ATLAS_MAX_HEIGHT = 1024
/** 剧情与存读档位图字体均包含的常用 ASCII 与基础标点。 */
const DEFAULT_BITMAP_FONT_CHARS =
  '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()-_=+/□:七… '

function uniqueChars(input: string): string {
  const ordered: string[] = []
  const seen = new Set<string>()
  for (const char of Array.from(input || '')) {
    if (!char || char === '\r' || char === '\n') continue
    if (seen.has(char)) continue
    seen.add(char)
    ordered.push(char)
  }
  return ordered.join('')
}

function appendChars(buffer: string[], value: string): void {
  if (!value) return
  const sanitized = stripEmoji(value)
  if (!sanitized) return
  buffer.push(sanitized)
}

function collectEventStrings(events: EventBase[] | null | undefined, output: string[]): void {
  if (!Array.isArray(events)) return
  for (const event of events) {
    if (!event) continue
    switch (event.Code) {
      case EventID.Text: {
        const typed = event as EventText
        appendChars(output, typed.RoleName || typed.Parameter?.[0] || '')
        appendChars(output, typed.Content || typed.Parameter?.[2] || '')
        break
      }
      case EventID.SendAirBubble: {
        const typed = event as EventSendAirBubble
        appendChars(output, typed.Name)
        if (typed.MessageType === 1) {
          appendChars(output, typed.MessageConten)
        }
        break
      }
      case EventID.TextDif: {
        const typed = event as EventTextDif
        for (const item of typed.TextDifChoice) appendChars(output, item)
        break
      }
      case EventID.TextDifEx: {
        const typed = event as EventTextDifEx
        for (const item of typed.ChoiceText) appendChars(output, item)
        break
      }
      case EventID.TextDifEx2: {
        const typed = event as EventTextDifEx2
        for (const item of typed.ChoiceText) appendChars(output, item)
        break
      }
      case EventID.TextChoice: {
        const typed = event as EventTextChoice
        appendChars(output, typed.Content)
        break
      }
      case EventID.String: {
        const typed = event as EventString
        appendChars(output, typed.Content)
        break
      }
      case EventID.CallMenu: {
        const typed = event as EventCallMenu
        if (typed.Number === 10014) {
          const parts = typed.Input.split(',')
          for (let index = 0; index < parts.length; index += 3) {
            appendChars(output, parts[index] ?? '')
          }
        }
        break
      }
      case EventID.CallScene: {
        const typed = event as EventCallScene
        for (const item of typed.inputParams) {
          if (item.type === '6') {
            appendChars(output, item.value)
          }
        }
        break
      }
      default:
        break
    }
  }
}

/** 遍历工程剧情树全部节点（rootMaps + Maps，按 Id 去重）。 */
function forEachStoryMap(project: MainProject, visit: (map: MapInfo) => void): void {
  const visited = new Set<number>()
  const walk = (map: MapInfo): void => {
    if (!map || visited.has(map.Id)) return
    visited.add(map.Id)
    visit(map)
    for (const child of map.Childs ?? []) {
      walk(child)
    }
  }
  const roots = project.storyTree.rootMaps?.length ? project.storyTree.rootMaps : project.storyTree.Maps
  for (const map of roots) {
    walk(map)
  }
  for (const map of project.storyTree.Maps) {
    walk(map)
  }
}

function collectStoryTreeStrings(project: MainProject, output: string[]): void {
  forEachStoryMap(project, (map) => {
    appendChars(output, map.Name)
    collectEventStrings(project.storyTree.loadEvent(map.Id), output)
  })
}

/** 存读档独立位图字体包所需字符：常用符号 + 剧情名称（不含事件正文）。 */
function collectSaveLoadBitmapFontText(project: MainProject): string {
  const chunks: string[] = [DEFAULT_BITMAP_FONT_CHARS]
  forEachStoryMap(project, (map) => {
    appendChars(chunks, map.Name)
  })
  return uniqueChars(chunks.join(''))
}

/**
 * 收集剧情位图字体所需的全部字符（事件文本、系统文案、剧情名等）。
 * @param project 当前工程
 */
export function collectProjectBitmapFontText(project: MainProject): string {
  const chunks: string[] = [DEFAULT_BITMAP_FONT_CHARS]
  appendChars(chunks, project.systemData.totalSetting.AuthorWords)
  collectStoryTreeStrings(project, chunks)

  for (const item of project.systemData.CG.CGs) {
    appendChars(chunks, item.Name)
    appendChars(chunks, item.Message)
  }
  for (const item of project.systemData.BGM.BGMs) {
    appendChars(chunks, item.Name)
    appendChars(chunks, item.Message)
  }
  appendChars(chunks, project.systemData.BGM.NoName)

  for (const ui of project.systemData.customUI.CUIList) {
    collectEventStrings(ui.LoadEvent, chunks)
    collectEventStrings(ui.AfterEvent, chunks)
    for (const control of ui.ControlList) {
      collectEventStrings(control.EventList, chunks)
    }
  }

  for (const button of project.systemData.floatButton.FBtnList) {
    collectEventStrings(button.EventList, chunks)
  }

  return uniqueChars(chunks.join(''))
}

function rasterizeGlyph(params: {
  char: string
  fontFamily: string
  fontSize: number
  measuredWidth: number
  measuredHeight: number
  baselineAscent: number
  cellWidth: number
  cellHeight: number
}): { width: number; height: number; bitmap: HTMLCanvasElement } {
  const advanceWidth = Math.max(1, params.measuredWidth || params.fontSize || 1)
  const rasterHeight = Math.max(1, params.measuredHeight || params.fontSize || 1)
  const bitmapWidth = Math.max(1, params.cellWidth || advanceWidth)
  const bitmapHeight = Math.max(1, params.cellHeight || rasterHeight)
  const extraX = Math.max(8, Math.ceil(params.fontSize * 1.5))
  const extraY = Math.max(4, Math.ceil(params.fontSize * 0.75))

  const scratchCanvas = document.createElement('canvas')
  scratchCanvas.width = bitmapWidth + extraX * 2
  scratchCanvas.height = bitmapHeight + extraY * 2
  const scratchCtx = scratchCanvas.getContext('2d')
  if (!scratchCtx) {
    throw new Error('浏览器不支持 2D Canvas，无法栅格化位图字体')
  }
  scratchCtx.clearRect(0, 0, scratchCanvas.width, scratchCanvas.height)
  scratchCtx.textBaseline = 'alphabetic'
  scratchCtx.font = `${params.fontSize}px ${formatCanvasFontFamily(params.fontFamily)}`
  scratchCtx.fillStyle = '#ffffff'
  const drawX = extraX
  const drawY = extraY + Math.max(1, params.baselineAscent || params.fontSize)
  scratchCtx.fillText(params.char || 'NULL', drawX, drawY)
  scratchCtx.globalAlpha = 180 / 255
  scratchCtx.fillText(params.char || 'NULL', drawX, drawY)
  scratchCtx.globalAlpha = 1

  const bitmap = document.createElement('canvas')
  bitmap.width = bitmapWidth
  bitmap.height = bitmapHeight
  const bitmapCtx = bitmap.getContext('2d')
  if (!bitmapCtx) {
    throw new Error('浏览器不支持 2D Canvas，无法裁切位图字体')
  }
  bitmapCtx.clearRect(0, 0, bitmap.width, bitmap.height)
  bitmapCtx.drawImage(
    scratchCanvas,
    extraX,
    extraY,
    bitmapWidth,
    bitmapHeight,
    0,
    0,
    bitmapWidth,
    bitmapHeight,
  )
  return {
    width: advanceWidth,
    height: rasterHeight,
    bitmap,
  }
}

function decodeBase64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

function canvasToBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(async (blob) => {
        if (blob) {
          resolve(new Uint8Array(await BrowserFileUtils.blobToArrayBuffer(blob)))
          return
        }
        try {
          const dataUrl = canvas.toDataURL('image/png')
          const base64 = dataUrl.split(',', 2)[1] || ''
          if (!base64) {
            reject(new Error(`位图字体页面导出失败：空图像数据（${canvas.width}x${canvas.height}）`))
            return
          }
          resolve(decodeBase64ToBytes(base64))
        } catch {
          reject(new Error(`位图字体页面导出失败：无法导出 PNG（${canvas.width}x${canvas.height}）`))
        }
      }, 'image/png')
    } catch {
      reject(new Error(`位图字体页面导出失败：浏览器导出接口异常（${canvas.width}x${canvas.height}）`))
    }
  })
}

function writeXfiChar(writer: FileWriter, char: string): void {
  const codeUnit = String(char || '\0').charCodeAt(0) || 0
  writer.writeBytes(new Uint8Array([codeUnit & 0xff, (codeUnit >> 8) & 0xff]))
}

function buildXfiBytes(params: {
  rows: number
  cols: number
  cellWidth: number
  cellHeight: number
  glyphs: BitmapFontGlyph[]
}): Uint8Array {
  const writer = new FileWriter()
  writer.writeInt(params.rows)
  writer.writeInt(params.cols)
  writer.writeInt(params.cellWidth)
  writer.writeInt(params.cellHeight)
  writer.writeInt(params.glyphs.length)
  for (const glyph of params.glyphs) {
    writer.writeInt(glyph.index)
    writer.writeInt(glyph.width)
    writer.writeInt(glyph.height)
    writeXfiChar(writer, glyph.char)
  }
  return writer.toUint8Array()
}

function collectMergedBitmapFontText(params: {
  project: MainProject
  previousExtraText?: string
  extraText?: string
}): string {
  return uniqueChars(
    [
      collectProjectBitmapFontText(params.project),
      params.previousExtraText ?? '',
      params.extraText ?? '',
    ].join(''),
  )
}

/** 工程所需且尚未生成过的字符（按 mergedText 首次出现顺序）。 */
function listMissingChars(mergedText: string, glyphs: BitmapFontGlyph[]): string {
  const existingChars = new Set<string>()
  for (const glyph of glyphs) {
    if (glyph.char) existingChars.add(glyph.char)
  }
  const ordered: string[] = []
  const seen = new Set<string>()
  for (const char of Array.from(mergedText)) {
    if (!char || char === '\r' || char === '\n') continue
    if (existingChars.has(char) || seen.has(char)) continue
    seen.add(char)
    ordered.push(char)
  }
  return ordered.join('')
}

function canReuseBitmapFontPackage(
  existing: BitmapFontPackage,
  sourceFontKey: string,
  fontName: string,
  fontSize: number,
): boolean {
  return (
    existing.sourceFontKey === sourceFontKey
    && existing.fontName === fontName
    && existing.fontSize === fontSize
    && existing.pageLayoutFixed === true
    && existing.glyphs.length > 0
    && existing.files.some((item) => item.kind === 'xfi')
  )
}

function createBitmapFontMeasureContext(
  fontSize: number,
  fontFamily: string,
): CanvasRenderingContext2D {
  const measureCanvas = document.createElement('canvas')
  measureCanvas.width = 16
  measureCanvas.height = 16
  const measureCtx = measureCanvas.getContext('2d')
  if (!measureCtx) {
    throw new Error('浏览器不支持 2D Canvas，无法生成位图字体')
  }
  measureCtx.textBaseline = 'alphabetic'
  measureCtx.font = `${fontSize}px ${formatCanvasFontFamily(fontFamily)}`
  return measureCtx
}

function needsLargerCellForChars(
  existing: BitmapFontPackage,
  chars: string,
  measureCtx: CanvasRenderingContext2D,
  fontSize: number,
  fontFamily: string,
): boolean {
  for (const char of Array.from(chars)) {
    const size = measureBitmapFontTextWithContext(measureCtx, char, fontSize, fontFamily)
    if (size.width > existing.cellWidth || size.height > existing.cellHeight) {
      return true
    }
  }
  return false
}

function sortPackagePageFiles(files: BitmapFontFile[], fileBase: string): BitmapFontFile[] {
  return files
    .filter((item) => item.kind === 'page' && item.fileName.toLowerCase().startsWith(`${fileBase.toLowerCase()}$`))
    .slice()
    .sort((a, b) => {
      const pageA = Number(/\.(\d+)\.png$/i.exec(a.fileName)?.[1] ?? 0)
      const pageB = Number(/\.(\d+)\.png$/i.exec(b.fileName)?.[1] ?? 0)
      return pageA - pageB
    })
}

function toArrayBufferFromBytes(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

async function bytesToCanvas(bytes: Uint8Array): Promise<HTMLCanvasElement> {
  const blob = new Blob([toArrayBufferFromBytes(bytes)], { type: 'image/png' })
  const url = URL.createObjectURL(blob)
  try {
    const image = new Image()
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('位图字体页图像解码失败'))
      image.src = url
    })
    const canvas = document.createElement('canvas')
    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('浏览器不支持 2D Canvas，无法解码位图字体页')
    }
    ctx.drawImage(image, 0, 0)
    return canvas
  } finally {
    URL.revokeObjectURL(url)
  }
}

function createEmptyPageCanvas(cols: number, rows: number, cellWidth: number, cellHeight: number): HTMLCanvasElement {
  const pageCanvas = document.createElement('canvas')
  pageCanvas.width = cols * cellWidth
  pageCanvas.height = rows * cellHeight
  const pageCtx = pageCanvas.getContext('2d')
  if (!pageCtx) {
    throw new Error('浏览器不支持 2D Canvas，无法创建位图字体页')
  }
  pageCtx.clearRect(0, 0, pageCanvas.width, pageCanvas.height)
  return pageCanvas
}

async function buildBitmapFontPackageIncremental(params: {
  existing: BitmapFontPackage
  sourceFontKey: string
  fontName: string
  fontSize: number
  fontFamily: string
  deltaText: string
  measureCtx: CanvasRenderingContext2D
}): Promise<BitmapFontPackage> {
  const existing = params.existing
  const fontName = params.fontName.trim() || params.fontFamily || 'sans-serif'
  const fontFamily = params.fontFamily.trim() || fontName
  const fontSize = clampBitmapFontSize(params.fontSize)
  const { cols, rows, cellWidth, cellHeight } = existing
  if (!cols || !rows || !cellWidth || !cellHeight) {
    throw new Error('位图字体包缺少栅格参数，无法增量生成')
  }

  const measureCtx = params.measureCtx
  let baselineAscent = 1
  const slotsPerPage = cols * rows
  const fileBase = toBitmapFontPackageKey(fontName, fontSize)
  const pageFiles = sortPackagePageFiles(existing.files, fileBase)
  const pageCanvases: HTMLCanvasElement[] = []
  for (const pageFile of pageFiles) {
    pageCanvases.push(await bytesToCanvas(pageFile.bytes))
  }

  const glyphs = existing.glyphs.map((item) => new BitmapFontGlyph(item))
  const firstModifiedPage = Math.floor(glyphs.length / slotsPerPage)
  let glyphIndex = glyphs.length

  for (const char of Array.from(params.deltaText)) {
    const measured = measureBitmapFontTextWithContext(measureCtx, char, fontSize, fontFamily)
    baselineAscent = Math.max(baselineAscent, measured.ascent || 0)
    const raster = rasterizeGlyph({
      char,
      fontFamily,
      fontSize,
      measuredWidth: measured.width,
      measuredHeight: measured.height,
      baselineAscent,
      cellWidth,
      cellHeight,
    })
    const pageIdx = Math.floor(glyphIndex / slotsPerPage)
    while (pageCanvases.length <= pageIdx) {
      pageCanvases.push(createEmptyPageCanvas(cols, rows, cellWidth, cellHeight))
    }
    const pageCanvas = pageCanvases[pageIdx]!
    const pageCtx = pageCanvas.getContext('2d')
    if (!pageCtx) {
      throw new Error('浏览器不支持 2D Canvas，无法绘制位图字体页')
    }
    const slotInPage = glyphIndex % slotsPerPage
    const row = Math.floor(slotInPage / cols)
    const col = slotInPage % cols
    pageCtx.drawImage(raster.bitmap, col * cellWidth, row * cellHeight, cellWidth, cellHeight)
    glyphs.push(new BitmapFontGlyph({
      char,
      index: glyphIndex,
      width: raster.width,
      height: raster.height,
    }))
    glyphIndex += 1
  }

  const files: BitmapFontFile[] = []
  for (let pageIdx = 0; pageIdx < pageCanvases.length; pageIdx += 1) {
    const pageNumber = pageIdx + 1
    const pageCanvas = pageCanvases[pageIdx]!
    const reuseBytes = pageIdx < firstModifiedPage ? pageFiles[pageIdx]?.bytes : null
    files.push(new BitmapFontFile({
      fileName: `${fileBase}$${pageNumber}.png`,
      kind: 'page',
      mimeType: 'image/png',
      bytes: reuseBytes ?? await canvasToBytes(pageCanvas),
    }))
  }

  files.push(new BitmapFontFile({
    fileName: `${fileBase}.xfi`,
    kind: 'xfi',
    mimeType: 'application/octet-stream',
    bytes: buildXfiBytes({
      rows,
      cols,
      cellWidth,
      cellHeight,
      glyphs,
    }),
  }))

  return new BitmapFontPackage({
    fontName,
    fontSize,
    sourceFontKey: params.sourceFontKey,
    rows,
    cols,
    cellWidth,
    cellHeight,
    pageLayoutFixed: true,
    glyphs,
    files,
  })
}

async function buildBitmapFontPackageFull(params: {
  sourceFontKey: string
  fontName: string
  fontSize: number
  fontFamily: string
  mergedText: string
}): Promise<BitmapFontPackage> {
  const fontName = params.fontName.trim() || params.fontFamily || 'sans-serif'
  const fontFamily = params.fontFamily.trim() || fontName
  const rawFontSize = Number(params.fontSize || 30)
  const fontSize = clampBitmapFontSize(rawFontSize)

  const measureCtx = createBitmapFontMeasureContext(fontSize, fontFamily)

  const glyphs: BitmapFontGlyph[] = []
  const rasterBitmaps: HTMLCanvasElement[] = []
  const measuredSizes: BitmapFontTextMetrics[] = []
  let maxGlyphWidth = 0
  let maxGlyphHeight = 0

  for (const char of Array.from(params.mergedText)) {
    const size = measureBitmapFontTextWithContext(measureCtx, char, fontSize, fontFamily)
    maxGlyphWidth = Math.max(maxGlyphWidth, size.width)
    maxGlyphHeight = Math.max(maxGlyphHeight, size.height)
    measuredSizes.push(size)
  }

  const cellWidth = Math.max(1, maxGlyphWidth || fontSize)
  const cellHeight = Math.max(1, maxGlyphHeight || fontSize)
  const baselineAscent = Math.max(
    1,
    ...measuredSizes.map((item) => item.ascent || 0),
  )
  for (const [index, char] of Array.from(params.mergedText).entries()) {
    const measured = measuredSizes[index] ?? { width: fontSize, height: fontSize, ascent: fontSize, descent: 0 }
    const raster = rasterizeGlyph({
      char,
      fontFamily,
      fontSize,
      measuredWidth: measured.width,
      measuredHeight: measured.height,
      baselineAscent,
      cellWidth,
      cellHeight,
    })
    glyphs.push(new BitmapFontGlyph({
      char,
      index,
      width: raster.width,
      height: raster.height,
    }))
    rasterBitmaps.push(raster.bitmap)
  }

  const cols = Math.max(1, Math.floor(ATLAS_MAX_WIDTH / cellWidth))
  const rows = Math.max(1, Math.floor(ATLAS_MAX_HEIGHT / cellHeight))
  const slotsPerPage = cols * rows
  const fileBase = toBitmapFontPackageKey(fontName, fontSize)
  const files: BitmapFontFile[] = []
  let glyphIndex = 0
  let page = 1

  while (glyphIndex < glyphs.length) {
    const pageCanvas = createEmptyPageCanvas(cols, rows, cellWidth, cellHeight)
    const pageCtx = pageCanvas.getContext('2d')
    if (!pageCtx) {
      throw new Error('浏览器不支持 2D Canvas，无法绘制位图字体页')
    }
    for (let slot = 0; slot < slotsPerPage && glyphIndex < glyphs.length; slot += 1) {
      const row = Math.floor(slot / cols)
      const col = slot % cols
      const rasterBitmap = rasterBitmaps[glyphIndex]!
      pageCtx.drawImage(rasterBitmap, col * cellWidth, row * cellHeight, cellWidth, cellHeight)
      glyphIndex += 1
    }

    files.push(new BitmapFontFile({
      fileName: `${fileBase}$${page}.png`,
      kind: 'page',
      mimeType: 'image/png',
      bytes: await canvasToBytes(pageCanvas),
    }))
    page += 1
  }

  files.push(new BitmapFontFile({
    fileName: `${fileBase}.xfi`,
    kind: 'xfi',
    mimeType: 'application/octet-stream',
    bytes: buildXfiBytes({
      rows,
      cols,
      cellWidth,
      cellHeight,
      glyphs,
    }),
  }))

  return new BitmapFontPackage({
    fontName,
    fontSize,
    sourceFontKey: params.sourceFontKey,
    rows,
    cols,
    cellWidth,
    cellHeight,
    pageLayoutFixed: true,
    glyphs,
    files,
  })
}

/** 同字体源与包名：所需字符齐全则跳过；仅补新字则增量；新字撑大 cell 则全量。 */
async function buildBitmapFontPackage(params: {
  sourceFontKey: string
  fontName: string
  fontSize: number
  fontFamily: string
  mergedText: string
  existing?: BitmapFontPackage | null
}): Promise<BitmapFontPackage> {
  const fontName = params.fontName.trim() || params.fontFamily || 'sans-serif'
  const fontFamily = params.fontFamily.trim() || fontName
  const fontSize = clampBitmapFontSize(params.fontSize)
  const sourceFontKey = params.sourceFontKey.trim()
  const existing = params.existing

  if (existing && canReuseBitmapFontPackage(existing, sourceFontKey, fontName, fontSize)) {
    const missing = listMissingChars(params.mergedText, existing.glyphs)
    if (!missing) {
      return existing
    }
    const measureCtx = createBitmapFontMeasureContext(fontSize, fontFamily)
    if (!needsLargerCellForChars(existing, missing, measureCtx, fontSize, fontFamily)) {
      return buildBitmapFontPackageIncremental({
        existing,
        sourceFontKey,
        fontName,
        fontSize,
        fontFamily,
        deltaText: missing,
        measureCtx,
      })
    }
  }

  return buildBitmapFontPackageFull({
    sourceFontKey,
    fontName,
    fontSize,
    fontFamily,
    mergedText: params.mergedText,
  })
}

function disposePackageFiles(pack: BitmapFontPackage): void {
  for (const file of pack.files) {
    file.revokeObjectUrl()
  }
}

/**
 * 测试/发布前生成工程位图字体数据。
 * 剧情包使用全文集；存读档独立包使用常用符号 + 剧情名；规格一致时共用 xfi。
 */
export async function buildBitmapFontData(params: {
  project: MainProject
  currentRoot: DataFileMap | null
  extraText?: string
  existingBitmapFont?: BitmapFontData | null
}): Promise<BitmapFontData> {
  const project = params.project
  const previous = params.existingBitmapFont
  const nextExtraText = uniqueChars([previous?.extraText ?? '', params.extraText ?? ''].join(''))
  const mergedText = collectMergedBitmapFontText({
    project,
    previousExtraText: previous?.extraText,
    extraText: params.extraText,
  })
  const mainSpec = resolveMainFontSpec(project, params.currentRoot)
  const mainPackageKey = toBitmapFontPackageKey(mainSpec.packageFontName, mainSpec.fontSize)
  const saveLoadText = uniqueChars(
    [
      collectSaveLoadBitmapFontText(project),
      previous?.extraText ?? '',
      params.extraText ?? '',
    ].join(''),
  )
  const targets = collectBitmapFontTargets(project, params.currentRoot)
  const packages: BitmapFontPackage[] = []

  for (const target of targets) {
    const sourceFontKey = String(target.sourceFontName || '').trim()
    const packageKey = toBitmapFontPackageKey(target.packageFontName, target.fontSize)
    const existingPkg =
      previous?.packages.find(
        (item) =>
          item.sourceFontKey === sourceFontKey
          && toBitmapFontPackageKey(item.fontName, item.fontSize) === packageKey,
      ) ?? null
    const fontFamily = params.currentRoot
      ? (await ensureFontLoadedFromVirtualResource({
          root: params.currentRoot,
          storedPath: target.sourceFontName,
        })).family
      : target.sourceFontName || target.packageFontName || 'sans-serif'
    const textForPackage = packageKey === mainPackageKey ? mergedText : saveLoadText
    const nextPkg = await buildBitmapFontPackage({
      sourceFontKey,
      fontName: target.packageFontName,
      fontSize: target.fontSize,
      fontFamily,
      mergedText: textForPackage,
      existing: existingPkg,
    })
    if (existingPkg && nextPkg !== existingPkg) {
      disposePackageFiles(existingPkg)
    }
    packages.push(nextPkg)
  }

  for (const orphan of previous?.packages ?? []) {
    if (packages.includes(orphan)) continue
    disposePackageFiles(orphan)
  }
  for (const file of previous?.extraFiles ?? []) {
    file.revokeObjectUrl()
  }

  return new BitmapFontData({
    packages,
    extraFiles: [
      new BitmapFontFile({
        fileName: 'font.list',
        kind: 'meta',
        mimeType: 'application/octet-stream',
        bytes: buildFontListBytes(project, targets, params.currentRoot),
      }),
      new BitmapFontFile({
        fileName: 'saveload.list',
        kind: 'meta',
        mimeType: 'application/octet-stream',
        bytes: buildSaveLoadListBytes(project, targets, params.currentRoot),
      }),
    ],
    extraText: nextExtraText,
  })
}
